import { useEffect } from 'react';
// Store hooks
import { useAppDispatch, useAppSelector } from '@/store/hooks';

// Services
import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { useCartContext } from '@/services/cart/CartContext';

// Components
import PageHeader from '@/components/pageHeader/PageHeader';
import MarketingPreferences from '@/components/marketingPreferences/MarketingPreferences';
import PaymentErrorText from './PaymentErrorText';

// Utilities
import { useValidateTransactionComplete } from './useValidateTransactionComplete';
import { setErrorMessage, clearErrorMessage } from './transactionSlice';
import RouteProps from '@/types/shared-types';

// GTM
import { GTM_EVENTS, setGtmEventChange } from '@/services/gtm/gtmSlice';

function PaymentSuccess({ totalSteps, currencySymbol }: RouteProps) {
  const dispatch = useAppDispatch();
  const transactionState = useAppSelector(state => state.transaction);
  const userDetailsState = useAppSelector(state => state.userDetails);
  const { cartData, cartId } = useCartContext();

  const { errorMessage } = transactionState;
  const { firstName } = userDetailsState;

  // Validate state is as expected, otherwise redirect to index
  useValidateTransactionComplete();

  const journeyNavigate = useJourneyNavigate();

  const onMarketingPreferencesSuccess = () => {
    dispatch(clearErrorMessage());
    journeyNavigate('submitted');
  };

  const onMarketingPreferencesError = () => {
    dispatch(setErrorMessage('Error processing marketing preferences.'));
  };

  useEffect(() => {
    // We _shouldn't_ have got this far without either of these IDs, but add belt-and-braces check just in case:
    if ((transactionState.transactionId || transactionState.subscriptionId) && cartId && cartData) {
      dispatch(setGtmEventChange({ eventType: GTM_EVENTS.AFTER_PAYMENT_PAGE, cartId, cartData }));
    }
  }, [transactionState, dispatch, cartId, cartData]);

  /* Render purely the error message if there is one: */
  if (errorMessage) {
    return <PaymentErrorText />;
  }

  // Else render our 'success' content:
  return (
    <>
      <PageHeader
        // TO DO: This line below needs sorting in another PR, we should not have that hardcoded
        currentStep={5}
        totalSteps={totalSteps}
        headerText={firstName ? `Thank you, ${firstName}` : 'Thank you'}
        initialText="We have successfully received your generous donation of"
        currencySymbol={currencySymbol}
      />

      <MarketingPreferences
        onSuccess={onMarketingPreferencesSuccess}
        onError={onMarketingPreferencesError}
      />
    </>
  );
}

export default PaymentSuccess;
